import { useState, useEffect, FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { ParlayWidget } from '../components/parlay/ParlayWidget';
import { useEvent, useUser, useParlay, useAuth } from '../store';
import { loadEvent as loadEventFromStorage, saveEvent, calculatePayout, formatOdds, formatCurrency, parseBetAmount, validateBetAmount, getQuickBetAmounts, mergeTemplateIntoEvent } from '../utils';
import type { Bet, FightMarket, MarketOutcome } from '../types';

/**
 * Props Bet Page
 * Page for placing prop bets on a fight's various markets
 */
export function PropsBetPage() {
  const { eventId, fightId } = useParams<{ eventId: string; fightId: string }>();
  const navigate = useNavigate();
  const { event, loadEvent, getFight, addBet, updateParticipant } = useEvent();
  const { currentParticipant: currentParticipantFromUser, setCurrentParticipant } = useUser();
  const { currentUser } = useAuth();
  const { isActive: parlayActive, startParlay, addLeg, hasFight } = useParlay();

  const [selectedMarket, setSelectedMarket] = useState<string | null>(null);
  const [selectedOutcome, setSelectedOutcome] = useState<MarketOutcome | null>(null);
  const [showBetForm, setShowBetForm] = useState(false);
  const [betAmount, setBetAmount] = useState('');
  const [betError, setBetError] = useState('');
  const [loading, setLoading] = useState(!event);

  // Find current participant from event (same logic as DashboardPage)
  const currentParticipant = (() => {
    if (!event) return currentParticipantFromUser;
    
    // Try to find by participant ID
    if (currentParticipantFromUser?.id) {
      const byId = event.participants.find(p => p.id === currentParticipantFromUser.id);
      if (byId) return byId;
    }
    
    // Try to find by userId
    if (currentUser?.id) {
      const byUserId = event.participants.find(p => p.userId === currentUser.id);
      if (byUserId) return byUserId;
    }
    
    // Try to find by username match
    if (currentUser?.username) {
      const byUsername = event.participants.find(
        p => p.name.toLowerCase() === currentUser.username.toLowerCase()
      );
      if (byUsername) return byUsername;
    }
    
    return currentParticipantFromUser;
  })();

  useEffect(() => {
    if (!eventId) {
      navigate('/');
      return;
    }

    // Only load if event not already in context
    if (event && event.id === eventId) {
      setLoading(false);
      return;
    }

    const loadEventData = async () => {
      setLoading(true);
      try {
        let eventData = await loadEventFromStorage(eventId);
        if (eventData) {
          eventData = await mergeTemplateIntoEvent(eventData);
          loadEvent(eventData); // Load into context!
        } else {
          navigate('/');
        }
      } catch (err) {
        console.error('Error loading event:', err);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    loadEventData();
  }, [eventId, event, loadEvent, navigate]);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [fightId]);

  // Sync found participant to UserContext
  useEffect(() => {
    if (currentParticipant && (!currentParticipantFromUser || currentParticipant.id !== currentParticipantFromUser.id)) {
      setCurrentParticipant(currentParticipant);
    }
  }, [currentParticipant, currentParticipantFromUser, setCurrentParticipant]);

  if (loading || !event || !fightId) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Redirect if user is not a participant
  if (!currentParticipant) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <Card padding="lg">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-primary-text mb-4">Not a Participant</h1>
            <p className="text-secondary-text mb-4">You must join this event to place bets.</p>
            <Button variant="primary" onClick={() => navigate(`/event/${eventId}`)}>
              Back to Dashboard
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const fight = getFight(fightId);
  if (!fight) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <Card padding="lg">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-primary-text mb-4">Fight Not Found</h1>
            <Button variant="secondary" onClick={() => navigate(`/event/${eventId}`)}>
              Back to Dashboard
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (!fight.markets || fight.markets.length === 0) {
    return (
      <div className="min-h-screen bg-primary-bg pb-20">
        <div className="bg-secondary-bg border-b border-border p-4 sticky top-0 z-10">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <h1 className="text-xl font-bold text-primary-text">Prop Bets</h1>
            <Button variant="secondary" size="sm" onClick={() => navigate(`/event/${eventId}`)}>
              Back
            </Button>
          </div>
        </div>
        <div className="max-w-4xl mx-auto p-4">
          <Card padding="lg">
            <div className="text-center py-8">
              <p className="text-secondary-text mb-4">No prop markets available for this fight</p>
              <p className="text-sm text-muted-text mb-4">
                {fight.fighters[0]?.name || 'TBD'} vs {fight.fighters[1]?.name || 'TBD'}
              </p>
              <Button variant="primary" onClick={() => navigate(`/event/${eventId}/bet/${fightId}`)}>
                Place Moneyline Bet Instead
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  const handleOutcomeSelect = (market: FightMarket, outcome: MarketOutcome) => {
    setSelectedMarket(market.key);
    setSelectedOutcome(outcome);
    setShowBetForm(true);
  };

  const handleStartParlay = () => {
    if (!selectedOutcome || !fight) return;
    
    if (betAmount.trim()) {
      setBetError('Leave parlay amount empty until final bet is made.');
      return;
    }
    
    const market = fight.markets?.find(m => m.key === selectedMarket);
    startParlay();
    addLeg({
      fightId: fight.id,
      fightName: `${fight.fighters[0]?.name || 'TBD'} vs ${fight.fighters[1]?.name || 'TBD'}`,
      selection: selectedOutcome.name,
      odds: selectedOutcome.odds,
      type: 'prop',
      marketName: market?.name,
    });
    
    navigate(`/event/${eventId}`);
  };

  const handleAddToParlay = () => {
    if (!selectedOutcome || !fight) return;
    
    // Check if this fight is already in the parlay
    if (hasFight(fight.id)) {
      setBetError('This fight is already in your parlay. You can only add one bet per fight.');
      return;
    }
    
    const market = fight.markets?.find(m => m.key === selectedMarket);
    try {
      addLeg({
        fightId: fight.id,
        fightName: `${fight.fighters[0]?.name || 'TBD'} vs ${fight.fighters[1]?.name || 'TBD'}`,
        selection: selectedOutcome.name,
        odds: selectedOutcome.odds,
        type: 'prop',
        marketName: market?.name,
      });
      
      navigate(`/event/${eventId}`);
    } catch (err: any) {
      setBetError(err.message || 'Failed to add to parlay');
    }
  };

  const handleBetSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBetError('');

    if (!selectedMarket || !selectedOutcome || !fight || !currentParticipant) {
      setBetError('Invalid selection');
      return;
    }

    const parsedAmount = parseBetAmount(betAmount);
    if (parsedAmount === null) {
      setBetError('Please enter a valid bet amount');
      return;
    }

    if (!validateBetAmount(parsedAmount, 1.0, currentParticipant.totalAvailable)) {
      if (parsedAmount < 1.0) {
        setBetError('Minimum bet is $1.00');
      } else if (parsedAmount > currentParticipant.totalAvailable) {
        setBetError('Insufficient funds');
      } else {
        setBetError('Invalid bet amount');
      }
      return;
    }

    // Create bet
    const bet: Bet = {
      id: `bet-${Date.now()}-${Math.random()}`,
      participantId: currentParticipant.id,
      fightId: fight.id,
      marketKey: selectedMarket,
      outcomeId: selectedOutcome.id,
      outcomeName: selectedOutcome.name,
      amount: parsedAmount,
      odds: selectedOutcome.odds,
      status: 'pending',
      placedAt: Date.now(),
    };

    // Add bet
    addBet(bet);

    // Update participant
    const updatedBets = [...(currentParticipant.bets || []), bet];
    const updatedParticipant = {
      ...currentParticipant,
      bets: updatedBets,
      totalAvailable: currentParticipant.totalAvailable - parsedAmount,
    };
    
    updateParticipant(currentParticipant.id, {
      bets: updatedBets,
      totalAvailable: currentParticipant.totalAvailable - parsedAmount,
    });

    // Save to localStorage
    const updatedEvent = {
      ...event,
      participants: event.participants.map((p) =>
        p.id === currentParticipant.id
          ? updatedParticipant
          : p
      ),
    };
    saveEvent(updatedEvent);

    // Update UserContext with the new participant data (including the bet)
    setCurrentParticipant(updatedParticipant);

    // Reset selection
    setSelectedMarket(null);
    setSelectedOutcome(null);
    setShowBetForm(false);
    setBetAmount('');
    setBetError('');

    // Show success message
    alert(`Bet placed: ${selectedOutcome.name} for ${formatCurrency(parsedAmount)}`);
  };

  const quickAmounts = getQuickBetAmounts(currentParticipant?.totalAvailable || 0);

  return (
    <div className="min-h-screen bg-primary-bg pb-20">
      {/* Header */}
      <div className="bg-secondary-bg border-b border-border p-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-primary-text">Prop Bets</h1>
            <p className="text-sm text-secondary-text">
              {fight.fighters[0]?.name || 'TBD'} vs {fight.fighters[1]?.name || 'TBD'}
            </p>
          </div>
        </div>
      </div>

      {/* Back Arrow */}
      <div className="max-w-4xl mx-auto px-4 pt-4">
        <button
          onClick={() => navigate(`/event/${eventId}`)}
          className="flex items-center gap-2 text-primary-blue hover:text-blue-400 transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="font-medium">Back to Dashboard</span>
        </button>
      </div>

      <div className="max-w-4xl mx-auto p-4">
        {showBetForm && selectedOutcome ? (
          <Card padding="lg" className="mb-6">
            <div className="mb-4">
              <Button variant="secondary" size="sm" onClick={() => {
                setShowBetForm(false);
                setSelectedOutcome(null);
                setSelectedMarket(null);
              }}>
                ← Back to Markets
              </Button>
            </div>
            <h2 className="text-lg font-semibold text-primary-text mb-2">Place Bet</h2>
            <div className="mb-4 p-4 bg-tertiary-bg rounded-lg">
              <p className="text-sm text-secondary-text mb-1">Market</p>
              <p className="font-medium text-primary-text">
                {fight.markets?.find((m) => m.key === selectedMarket)?.name || 'Unknown Market'}
              </p>
              <p className="text-sm text-secondary-text mb-1 mt-2">Outcome</p>
              <p className="font-medium text-primary-text">{selectedOutcome.name}</p>
              <p className="text-sm text-secondary-text mb-1 mt-2">Odds</p>
              <p className="font-medium text-primary-text">{formatOdds(selectedOutcome.odds)}</p>
              <p className="text-sm text-secondary-text mb-1 mt-2">Potential Payout</p>
              <p className="font-medium text-success-green">
                {formatCurrency(calculatePayout(100, selectedOutcome.odds))} (on $100 bet)
              </p>
            </div>
            <form onSubmit={handleBetSubmit} className="space-y-4">
              <Input
                label="Bet Amount"
                type="text"
                placeholder="Enter amount"
                value={betAmount}
                onChange={(e) => {
                  setBetAmount(e.target.value);
                  setBetError('');
                }}
                error={betError}
                helperText={`Available: ${formatCurrency(currentParticipant.totalAvailable)}`}
              />

              <div>
                <p className="text-sm text-secondary-text mb-2">Quick Amounts:</p>
                <div className="flex gap-2 flex-wrap items-center">
                  {quickAmounts.length > 0 && quickAmounts.map((amount) => (
                    <Button
                      key={amount}
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setBetAmount(amount.toString());
                        setBetError('');
                      }}
                    >
                      ${amount}
                    </Button>
                  ))}
                  
                  {/* Parlay Button - To the right of quick amounts */}
                  {!parlayActive ? (
                    <button
                      type="button"
                      onClick={handleStartParlay}
                      disabled={!selectedOutcome}
                      className={`px-4 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                        selectedOutcome
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      🎯 Start Parlay
                    </button>
                  ) : !hasFight(fight.id) ? (
                    <button
                      type="button"
                      onClick={handleAddToParlay}
                      disabled={!selectedOutcome}
                      className={`px-4 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                        selectedOutcome
                          ? 'bg-green-600 text-white hover:bg-green-700'
                          : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      ➕ Add to Parlay
                    </button>
                  ) : (
                    <span className="px-4 py-1.5 text-sm rounded-lg font-medium bg-gray-700 text-gray-500 cursor-not-allowed">
                      ✓ Already in Parlay
                    </span>
                  )}
                </div>
              </div>

              {selectedOutcome && (
                <div className="p-4 bg-tertiary-bg rounded-lg">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-secondary-text mb-1">Odds</p>
                      <p className="font-semibold text-primary-text">{formatOdds(selectedOutcome.odds)}</p>
                    </div>
                    {betAmount && parseBetAmount(betAmount) !== null && (
                      <div>
                        <p className="text-secondary-text mb-1">Potential Payout</p>
                        <p className="font-semibold text-success-green">
                          {formatCurrency(calculatePayout(parseBetAmount(betAmount)!, selectedOutcome.odds))}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t border-border">
                <Button
                  type="button"
                  variant="secondary"
                  fullWidth
                  onClick={() => {
                    setShowBetForm(false);
                    setSelectedOutcome(null);
                    setSelectedMarket(null);
                    setBetAmount('');
                    setBetError('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  fullWidth
                  disabled={!betAmount || parseBetAmount(betAmount) === null}
                >
                  Place Bet
                </Button>
              </div>
            </form>
          </Card>
        ) : (
          <div className="space-y-6">
            {fight.markets?.map((market) => (
              <Card key={market.key} padding="md">
                <h2 className="text-lg font-semibold text-primary-text mb-4">{market.name}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {market.outcomes.map((outcome) => (
                    <button
                      key={outcome.id}
                      onClick={() => handleOutcomeSelect(market, outcome)}
                      className="p-4 bg-tertiary-bg rounded-lg border-2 border-border hover:border-primary-blue hover:bg-primary-blue/5 transition-colors text-left"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-primary-text">{outcome.name}</p>
                        </div>
                        <div className="text-right ml-4">
                          <p className="font-bold text-primary-text text-lg">{formatOdds(outcome.odds)}</p>
                          <p className="text-xs text-secondary-text">American</p>
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-secondary-text">
                        Payout: {formatCurrency(calculatePayout(100, outcome.odds))} on $100
                      </div>
                    </button>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
      
      {/* Parlay Widget */}
      <ParlayWidget />
    </div>
  );
}

